import type { ViewStyle } from 'react-native'
import type { LayoutAnimationBuilder, LayoutPresetName } from './types'

type WebTransitionStyle = ViewStyle & {
  transitionProperty: string
  transitionDuration: string
  transitionDelay: string
  transitionTimingFunction: string
}

type InitialValues = { opacity?: number; transform?: ViewStyle['transform'] }
type LayoutAnimationState = {
  callback?: (finished: boolean) => void
  delayMs?: number
  durationMs?: number
  easingFn?: (value: number) => number
  initialValues?: InitialValues
}

const LAYOUT_TRANSITION_PROPERTY =
  'opacity, transform, width, height, min-width, min-height, max-width, max-height, margin, padding, top, right, bottom, left'
const APPEARANCE_TRANSITION_PROPERTY = 'opacity, transform'

const PRESET_VALUES: Record<
  LayoutPresetName,
  {
    from: Pick<ViewStyle, 'opacity' | 'transform'>
    to: Pick<ViewStyle, 'opacity' | 'transform'>
  }
> = {
  fadeInDown: {
    from: { opacity: 0, transform: [{ translateY: -16 }] },
    to: { opacity: 1, transform: [{ translateY: 0 }] },
  },
  fadeOutDown: {
    from: { opacity: 1, transform: [{ translateY: 0 }] },
    to: { opacity: 0, transform: [{ translateY: 16 }] },
  },
  fadeInUp: {
    from: { opacity: 0, transform: [{ translateY: 16 }] },
    to: { opacity: 1, transform: [{ translateY: 0 }] },
  },
  fadeInLeft: {
    from: { opacity: 0, transform: [{ translateX: -16 }] },
    to: { opacity: 1, transform: [{ translateX: 0 }] },
  },
  fadeInRight: {
    from: { opacity: 0, transform: [{ translateX: 16 }] },
    to: { opacity: 1, transform: [{ translateX: 0 }] },
  },
  fadeOutLeft: {
    from: { opacity: 1, transform: [{ translateX: 0 }] },
    to: { opacity: 0, transform: [{ translateX: -16 }] },
  },
  fadeOutRight: {
    from: { opacity: 1, transform: [{ translateX: 0 }] },
    to: { opacity: 0, transform: [{ translateX: 16 }] },
  },
  fadeOutUp: {
    from: { opacity: 1, transform: [{ translateY: 0 }] },
    to: { opacity: 0, transform: [{ translateY: -16 }] },
  },
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeOut: {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
  zoomIn: {
    from: { opacity: 0, transform: [{ scale: 0.92 }] },
    to: { opacity: 1, transform: [{ scale: 1 }] },
  },
  zoomOut: {
    from: { opacity: 1, transform: [{ scale: 1 }] },
    to: { opacity: 0, transform: [{ scale: 0.92 }] },
  },
}

function createLayoutAnimationBuilder(
  preset: LayoutPresetName,
  state: Partial<LayoutAnimationState> = {},
): LayoutAnimationBuilder {
  const durationMs = state.durationMs ?? 600
  const delayMs = state.delayMs ?? 0
  const easingFn = state.easingFn
  const callback = state.callback
  const initialValues = state.initialValues

  const builder: LayoutAnimationBuilder = {
    preset,
    durationMs,
    delayMs,
    easingFn,
    callback,
    duration(nextDurationMs: number) {
      return createLayoutAnimationBuilder(preset, {
        callback,
        delayMs,
        durationMs: nextDurationMs,
        easingFn,
        ...(initialValues ? { initialValues } : {}),
      })
    },
    delay(nextDelayMs: number) {
      return createLayoutAnimationBuilder(preset, {
        callback,
        delayMs: nextDelayMs,
        durationMs,
        easingFn,
        ...(initialValues ? { initialValues } : {}),
      })
    },
    easing(nextEasingFn: (value: number) => number) {
      return createLayoutAnimationBuilder(preset, {
        callback,
        delayMs,
        durationMs,
        easingFn: nextEasingFn,
        ...(initialValues ? { initialValues } : {}),
      })
    },
    withCallback(nextCallback: (finished: boolean) => void) {
      return createLayoutAnimationBuilder(preset, {
        callback: nextCallback,
        delayMs,
        durationMs,
        easingFn,
        ...(initialValues ? { initialValues } : {}),
      })
    },
    springify() {
      return builder
    },
    damping() {
      return builder
    },
    stiffness() {
      return builder
    },
    withInitialValues(values: InitialValues) {
      return createLayoutAnimationBuilder(preset, {
        callback,
        delayMs,
        durationMs,
        easingFn,
        initialValues: values,
      })
    },
  }

  return builder
}

export const FadeInDown = createLayoutAnimationBuilder('fadeInDown')
export const FadeOutDown = createLayoutAnimationBuilder('fadeOutDown')
export const FadeInUp = createLayoutAnimationBuilder('fadeInUp')
export const FadeInLeft = createLayoutAnimationBuilder('fadeInLeft')
export const FadeInRight = createLayoutAnimationBuilder('fadeInRight')
export const FadeOutUp = createLayoutAnimationBuilder('fadeOutUp')
export const FadeOutLeft = createLayoutAnimationBuilder('fadeOutLeft')
export const FadeOutRight = createLayoutAnimationBuilder('fadeOutRight')
export const FadeIn = createLayoutAnimationBuilder('fadeIn')
export const FadeOut = createLayoutAnimationBuilder('fadeOut')
export const ZoomIn = createLayoutAnimationBuilder('zoomIn')
export const ZoomOut = createLayoutAnimationBuilder('zoomOut')
export const BounceIn = createLayoutAnimationBuilder('zoomIn')
export const LinearTransition = createLayoutAnimationBuilder('fadeIn')
export const FlipInEasyY = createLayoutAnimationBuilder('fadeInUp')
export const FlipOutEasyY = createLayoutAnimationBuilder('fadeOutUp')

export function getLayoutAnimationStyles(
  animation: LayoutAnimationBuilder | undefined,
  phase: 'from' | 'to',
): ViewStyle {
  if (!animation) {
    return {}
  }

  return PRESET_VALUES[animation.preset][phase]
}

export function getLayoutTransitionStyle(animation: LayoutAnimationBuilder | undefined): ViewStyle {
  if (!animation) {
    return {}
  }

  return {
    transitionProperty: LAYOUT_TRANSITION_PROPERTY,
    transitionDuration: `${animation.durationMs}ms`,
    transitionDelay: `${animation.delayMs}ms`,
    transitionTimingFunction: animation.easingFn ? 'ease-out' : 'ease',
  } as WebTransitionStyle
}

function getAppearanceTransitionStyle(animation: LayoutAnimationBuilder): ViewStyle {
  return {
    transitionProperty: APPEARANCE_TRANSITION_PROPERTY,
    transitionDuration: `${animation.durationMs}ms`,
    transitionDelay: `${animation.delayMs}ms`,
    transitionTimingFunction: animation.easingFn ? 'ease-out' : 'ease',
  } as WebTransitionStyle
}

export function getLayoutAnimationFrameStyles(
  animation: LayoutAnimationBuilder | undefined,
  phase: 'enter' | 'exit',
): { initialStyle: ViewStyle; targetStyle: ViewStyle } {
  if (!animation) {
    return { initialStyle: {}, targetStyle: {} }
  }

  const preset = PRESET_VALUES[animation.preset]

  return {
    initialStyle: phase === 'enter' ? { ...preset.from, ...animationInitialValues(animation) } : preset.from,
    targetStyle: {
      ...(phase === 'enter' ? preset.to : preset.to),
      ...getAppearanceTransitionStyle(animation),
    },
  }
}

function animationInitialValues(animation: LayoutAnimationBuilder): InitialValues {
  return (animation as LayoutAnimationBuilder & { initialValues?: InitialValues }).initialValues ?? {}
}

export function runLayoutAnimation(
  animation: LayoutAnimationBuilder | undefined,
  phase: 'enter' | 'exit',
  onComplete?: () => void,
): { initialStyle: ViewStyle; targetStyle: ViewStyle } {
  if (!animation) {
    return { initialStyle: {}, targetStyle: {} }
  }

  const animationStyles = getLayoutAnimationFrameStyles(animation, phase)

  window.setTimeout(() => {
    animation.callback?.(true)
    onComplete?.()
  }, animation.delayMs + animation.durationMs)

  return animationStyles
}
